import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

// Route users to their role-specific home
const ROLE_HOME = {
  super_admin: '/admin',
  bookkeeper: '/bookkeeper',
  auditor: '/auditor',
  store_user: '/store',
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from || null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      const destination = fromPath || ROLE_HOME[user.role] || '/roles';
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-cream-100)] font-sans flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-[var(--radius-lg)] bg-[var(--color-warm-900)] text-[var(--color-cream-50)] flex items-center justify-center font-extrabold text-[22px] mx-auto mb-4 shadow-[var(--shadow-md)]">
            R
          </div>
          <h1 className="text-[24px] font-extrabold text-[var(--color-warm-900)] tracking-tight">
            Parts & Vendor Reconciliation Hub
          </h1>
          <p className="text-[13px] text-[var(--color-warm-600)] mt-1">
            Sign in to continue
          </p>
        </div>

        {/* Card */}
        <div className="bg-[var(--color-cream-50)] border border-[var(--color-warm-100)] rounded-[var(--radius-xl)] shadow-[var(--shadow-md)] p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              leftIcon={<Mail size={14} />}
              required
              autoFocus
              autoComplete="email"
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              leftIcon={<Lock size={14} />}
              required
              autoComplete="current-password"
            />

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[var(--color-danger-700)] text-[13px]">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              className="w-full"
              leftIcon={!loading && <LogIn size={16} />}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        <div className="text-center mt-6 text-[12px] text-[var(--color-warm-500)]">
          Need help? Contact Danish.
        </div>
      </div>
    </div>
  );
}
